import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAirlineQuestions } from '../utils/airlineQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight, Plane } from 'lucide-react';
import confetti from 'canvas-confetti';

const OPTION_COLORS = [
  'bg-sky-500 border-sky-700 hover:bg-sky-400',
  'bg-indigo-500 border-indigo-700 hover:bg-indigo-400',
  'bg-teal-500 border-teal-700 hover:bg-teal-400',
  'bg-rose-500 border-rose-700 hover:bg-rose-400',
];

export default function AirlinePlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getAirlineQuestions(10));
  }, []); // mount-only: generate once

  const currentQ = questions[currentIndex];

  const handleOptionClick = (idx) => {
    if (showAnswer) return;
    setSelectedOption(idx);
    setShowAnswer(true);

    if (idx === currentQ.correctIndex) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#0ea5e9', '#6366f1', '#f43f5e'] });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/airline-quiz/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-100 to-white">
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-sky-500" />
            <span data-testid="airline-score" className="font-heading text-2xl font-black text-gray-900">
              {score}/{questions.length}
            </span>
          </div>
          <span data-testid="airline-counter" className="font-body text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} of {questions.length}
          </span>
          <button data-testid="airline-quit" onClick={() => navigate('/airline-quiz')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200">
        <div
          data-testid="airline-progress"
          className="h-full bg-sky-500 rounded-r-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
        <p className="text-center font-heading text-xl sm:text-2xl font-black text-gray-900 mb-4">
          Guess the airline from the logo!
        </p>

        {/* Airline Image */}
        <div className="rounded-3xl overflow-hidden shadow-lg border-2 border-gray-100 mb-2 bg-white">
          <img
            data-testid="airline-image"
            src={currentQ.image}
            alt="Guess this airline"
            className="w-full h-48 sm:h-56 object-cover"
          />
        </div>

        {/* Hint */}
        <p className="text-center font-body text-sm text-gray-400 mb-4">
          Hint: {currentQ.hint} ({currentQ.country})
        </p>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {currentQ.options.map((opt, i) => {
            const optKey = `${currentQ.id}-opt-${opt}`;
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption && i !== currentQ.correctIndex) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            }

            return (
              <button
                key={optKey}
                data-testid={`airline-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer}
                className={`${OPTION_COLORS[i]} text-white font-bold text-sm sm:text-base py-4 px-4 rounded-2xl border-b-[5px] ${!showAnswer ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-center ${extraClass}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="airline-feedback-correct" className="font-heading text-xl font-black text-green-600 mb-3">
                Correct!
              </p>
            ) : (
              <p data-testid="airline-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-3">
                It&apos;s <span className="text-green-700">{currentQ.answer}</span>!
              </p>
            )}
            <button
              data-testid="airline-next"
              onClick={handleNext}
              className="bg-sky-500 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-sky-700 active:border-b-0 active:translate-y-[4px] transition-all hover:bg-sky-400 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
